/**
 * Modal para crear/editar Miembro directamente
 * Incluye datos personales completos + datos de membresía
 * Permite buscar miembros existentes antes de crear uno nuevo
 */

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Input,
  Select,
  Badge,
  DocumentUpload,
  useConfirm,
} from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import EpsSelect from "@/components/forms/EpsSelect";
import {
  X,
  Loader2,
  UserPlus,
  Search,
  User,
  Mail,
  ArrowLeft,
  Plus,
  CreditCard,
  FileText,
  AlertTriangle,
} from "lucide-react";
import {
  useCreateMember,
  useUpdateMember,
  useMembersByClub,
  useCreatePerson,
} from "@/hooks/usePersons";
import { personService } from "@/lib/services/persons";
import { memberService } from "@/lib/services/members";
import {
  memberTransferService,
  getDirectorContact,
} from "@/lib/services/memberTransfers";
import { clubService } from "@/lib/services/organization";
import {
  uploadMemberPhoto,
  uploadDocumentFront,
  uploadDocumentBack,
  uploadEpsDocument,
} from "@/lib/services/storage";
import type { EncryptionMeta } from "@/lib/services/crypto";
import { useSystemUsers } from "@/hooks/useUsers";
import { useClubs, useUnitsByClub } from "@/hooks/useOrganization";
import { useAcademicPeriods } from "@/hooks/useCatalogs";
import { useAuthStore } from "@/stores/authStore";
import { filterManageableClubs } from "@/lib/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { MemberRolesPanel } from "./MemberRolesPanel";
import type {
  MemberWithPerson,
  Member,
  Person,
  MemberRole,
  MemberClass,
  ClubType,
  MemberType,
  IdDocumentType,
  BloodType,
  Gender,
} from "@/types";
import {
  MEMBER_ROLE_LABELS,
  MEMBER_CLASS_LABELS,
  ID_DOCUMENT_TYPE_LABELS,
  BLOOD_TYPE_LABELS,
  GENDER_LABELS,
  CLUB_TYPE_LABELS,
} from "@/types";
import { Timestamp } from "firebase/firestore";
import { toast } from "react-hot-toast";
import {
  getClubTypeByAge,
  getAllowedClubTypesByAge,
  deriveSectionFromBirthDate,
  resolveSectionForClub,
  getActiveSections,
  isImplausibleBirthDate,
} from "@/lib/utils";

// Helper para separar código de país del teléfono
function extractCountryCode(phone: string | undefined): {
  code: string;
  number: string;
} {
  if (!phone) return { code: "+57", number: "" };

  // Si tiene espacio, separar por el primer espacio
  const spaceMatch = phone.match(/^(\+\d{1,4})\s+(.+)$/);
  if (spaceMatch) {
    return { code: spaceMatch[1], number: spaceMatch[2] };
  }

  // Sin espacio: intentar matchear contra códigos de país conocidos (más largos primero)
  if (phone.startsWith("+")) {
    const knownCodes = [
      "+593",
      "+55",
      "+54",
      "+56",
      "+58",
      "+52",
      "+51",
      "+34",
      "+57",
      "+1",
    ];
    for (const code of knownCodes) {
      if (phone.startsWith(code)) {
        return { code, number: phone.slice(code.length) };
      }
    }
    // Fallback: asumir código de 1-3 dígitos
    const fallback = phone.match(/^(\+\d{1,3})(.*)$/);
    if (fallback) return { code: fallback[1], number: fallback[2] };
  }

  return { code: "+57", number: phone };
}

// Constantes para dropdowns
const relationships = [
  { value: "Padre", label: "Padre" },
  { value: "Madre", label: "Madre" },
  { value: "Abuelo/a", label: "Abuelo/a" },
  { value: "Hermano/a", label: "Hermano/a" },
  { value: "Tío/a", label: "Tío/a" },
  { value: "Tutor Legal", label: "Tutor Legal" },
  { value: "Esposo/a", label: "Esposo/a" },
  { value: "Otro", label: "Otro" },
];

const countryCodes = [
  { code: "+57", flag: "🇨🇴", name: "Colombia" },
  { code: "+52", flag: "🇲🇽", name: "México" },
  { code: "+593", flag: "🇪🇨", name: "Ecuador" },
  { code: "+51", flag: "🇵🇪", name: "Perú" },
  { code: "+58", flag: "🇻🇪", name: "Venezuela" },
  { code: "+34", flag: "🇪🇸", name: "España" },
  { code: "+1", flag: "🇺🇸", name: "Estados Unidos" },
  { code: "+54", flag: "🇦🇷", name: "Argentina" },
  { code: "+56", flag: "🇨🇱", name: "Chile" },
  { code: "+55", flag: "🇧🇷", name: "Brasil" },
];

// Schema de validación completo
const memberSchema = z
  .object({
    // Información Personal
    firstName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
    lastName: z.string().min(2, "El apellido debe tener al menos 2 caracteres"),
    // memberType se deriva de birthDate, no se selecciona manualmente
    idDocumentType: z.enum(["CC", "TI", "CE", "PASAPORTE", "RC", "PPT"] as const),
    idDocumentNumber: z
      .string()
      .min(4, "El número de identificación es requerido"),
    bloodType: z.enum([
      "A+",
      "A-",
      "B+",
      "B-",
      "AB+",
      "AB-",
      "O+",
      "O-",
    ] as const),
    birthDate: z
      .string()
      .min(1, "La fecha de nacimiento es requerida")
      .refine(
        (s) => !isImplausibleBirthDate(s),
        "Revisa el año: la fecha no puede ser futura ni dar edad 0",
      ),
    gender: z.enum(["male", "female"] as const),
    phoneCountryCode: z.string().default("+57"),
    phone: z.string().min(7, "El teléfono es requerido"),
    healthInsurance: z.string().optional(),
    email: z.string().min(1, "El email es requerido").email("Email inválido"),
    photoUrl: z.string().optional(),
    photoPath: z.string().optional(),

    // Documentos
    documentFrontUrl: z.string().optional(),
    documentFrontPath: z.string().optional(),
    documentBackUrl: z.string().optional(),
    documentBackPath: z.string().optional(),
    epsDocumentUrl: z.string().optional(),
    epsDocumentPath: z.string().optional(),

    // Información de Contacto
    address: z.string().min(5, "La dirección es requerida"),
    emergencyContactName: z
      .string()
      .min(3, "El nombre del contacto es requerido"),
    emergencyContactRelationship: z.string().min(2, "La relación es requerida"),
    emergencyContactCountryCode: z.string().default("+57"),
    emergencyContactPhone: z
      .string()
      .min(7, "El teléfono del contacto es requerido"),

    // Datos de membresía
    clubId: z.string().min(1, "Debes seleccionar un club"),
    periodId: z.string().optional(),
    unitId: z.string().optional(),
    role: z.string().min(1, "Debes seleccionar un cargo"),
    clase: z.string().optional(),
    status: z.enum(["active", "inactive", "pending"]).default("active"),
  })
  .superRefine((val, ctx) => {
    // Formato de documento: CC/TI/RC son numéricos en Colombia. Bloquea
    // placeholders tipo "ACTA449" o números demasiado cortos. CE/PASAPORTE
    // permiten alfanumérico (no se validan aquí).
    const num = (val.idDocumentNumber || "").trim();
    if (
      ["CC", "TI", "RC"].includes(val.idDocumentType) &&
      !/^\d{5,15}$/.test(num)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["idDocumentNumber"],
        message:
          "Para CC/TI/RC el documento debe ser solo números (5 a 15 dígitos).",
      });
    }
  });

type MemberForm = z.infer<typeof memberSchema>;

interface MemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member?: MemberWithPerson | null;
}

// Clases disponibles según tipo de club
const classesByClubType: Record<ClubType, MemberClass[]> = {
  aventureros: [
    "abejita_laboriosa",
    "rayito_de_sol",
    "constructor",
    "manos_ayudadoras",
  ],
  conquistadores: [
    "amigo",
    "companero",
    "explorador",
    "orientador",
    "viajero",
    "guia",
  ],
  guias_mayores: [
    "guia_mayor",
    "guia_mayor_master",
    "guia_mayor_master_avanzado",
  ],
};

// Roles disponibles para miembros
const availableRoles: MemberRole[] = [
  "member",
  "director",
  "subdirector",
  "consejero",
  "instructor",
  "tesorero",
  "secretario",
];

// Helper para calcular edad
function calculateAge(birthDate: Date): string {
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  if (today.getDate() < birthDate.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }

  return `${years} años ${months} meses`;
}

export default function MemberModal({
  isOpen,
  onClose,
  member,
}: MemberModalProps) {
  const { user, profile } = useAuthStore();
  const confirm = useConfirm();
  const { isClubDirector, canSeeMember } = usePermissions();
  const createPersonMutation = useCreatePerson();
  const createMemberMutation = useCreateMember();
  const updateMutation = useUpdateMember();
  const isEditing = !!member;
  const memberRolesEnabled = useFeatureFlag("memberRolesEnabled", true);

  // Guard: si abren el modal en modo edición sobre un member fuera del scope
  // del director (otra sección u otro club), cerrar y notificar.
  useEffect(() => {
    if (!isOpen || !member) return;
    const ok = canSeeMember({
      clubId: member.clubId,
      section: member.section,
      staffSection: member.staffSection,
    });
    if (!ok) {
      toast.error("No tienes acceso a este miembro");
      onClose();
    }
  }, [isOpen, member, canSeeMember, onClose]);

  // Estados para el paso de búsqueda
  const [step, setStep] = useState<"search" | "form">(
    isEditing ? "form" : "search",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExistingMember, setSelectedExistingMember] =
    useState<MemberWithPerson | null>(null);
  const [selectedSystemUser, setSelectedSystemUser] = useState<{
    id: string;
    displayName: string;
    email: string;
    phone?: string;
  } | null>(null);

  // Estados para archivos pendientes de subir
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [pendingDocumentFile, setPendingDocumentFile] = useState<File | null>(
    null,
  );
  const [pendingDocumentBackFile, setPendingDocumentBackFile] =
    useState<File | null>(null);
  const [pendingEpsFile, setPendingEpsFile] = useState<File | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const { data: clubs } = useClubs();
  const { data: periods } = useAcademicPeriods();

  // Obtener el club del director si aplica
  const directorClubId =
    isClubDirector && profile?.directorOfClub?.id
      ? profile.directorOfClub.id
      : null;

  // Obtener todos los miembros del club para búsqueda
  const { data: allClubMembers } = useMembersByClub(directorClubId || "");

  // Obtener todos los usuarios del sistema para búsqueda
  const { data: allSystemUsers } = useSystemUsers();

  // IDs de miembros que ya están en el club (para excluirlos de usuarios del sistema)
  const existingMemberEmails = useMemo(() => {
    if (!allClubMembers) return new Set<string>();
    return new Set(
      allClubMembers.map((m) => m.person.email?.toLowerCase()).filter(Boolean),
    );
  }, [allClubMembers]);

  // Filtrar miembros del club por búsqueda
  const filteredMembers = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2 || !allClubMembers) return [];
    const term = searchTerm.toLowerCase();
    return allClubMembers
      .filter(
        (m) =>
          m.person.fullName?.toLowerCase().includes(term) ||
          m.person.firstName?.toLowerCase().includes(term) ||
          m.person.lastName?.toLowerCase().includes(term) ||
          m.person.email?.toLowerCase().includes(term) ||
          m.person.idDocumentNumber?.includes(term),
      )
      .slice(0, 3); // Máximo 3 resultados
  }, [searchTerm, allClubMembers]);

  // Filtrar usuarios del sistema que NO están en el club
  const filteredSystemUsers = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2 || !allSystemUsers) return [];
    const term = searchTerm.toLowerCase();
    return allSystemUsers
      .filter((u) => {
        // Excluir usuarios que ya están en el club
        if (existingMemberEmails.has(u.email?.toLowerCase() || ""))
          return false;
        // Buscar por nombre o email
        return (
          u.displayName?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term)
        );
      })
      .slice(0, 5); // Máximo 5 resultados
  }, [searchTerm, allSystemUsers, existingMemberEmails]);

  const formatDateForInput = (
    timestamp: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!timestamp) return "";
    const date =
      typeof timestamp === "object" && "toDate" in timestamp && timestamp.toDate
        ? timestamp.toDate()
        : new Date(timestamp as unknown as string | number | Date);
    return date.toISOString().split("T")[0];
  };

  // Helper para mapear género antiguo al nuevo
  const mapGender = (gender: string | undefined): "male" | "female" => {
    if (gender === "male" || gender === "female") return gender;
    if (gender === "M") return "male";
    if (gender === "F") return "female";
    return "male"; // default
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<MemberForm>({
    resolver: zodResolver(memberSchema),
    mode: "onTouched",
    defaultValues: member
      ? (() => {
          const p = member.person;
          const phoneData = extractCountryCode(p.phone);
          const emergencyPhoneData = extractCountryCode(
            p.emergencyContact?.phone,
          );
          return {
            firstName: p.firstName || "",
            lastName: p.lastName || "",
            idDocumentType:
              (p.idDocumentType?.toUpperCase() as
                | "CC"
                | "TI"
                | "CE"
                | "PASAPORTE"
        | "PPT"
                | "PPT"
                | "RC") || "TI",
            idDocumentNumber: p.idDocumentNumber || "",
            bloodType: p.bloodType || "O+",
            birthDate: formatDateForInput(p.birthDate),
            gender: mapGender(p.gender),
            phoneCountryCode: phoneData.code,
            phone: phoneData.number,
            healthInsurance: p.healthInsurance || "",
            email: p.email || "",
            photoUrl: p.photoUrl || "",
            photoPath: p.photoPath || "",
            documentFrontUrl: p.documentFrontUrl || "",
            documentFrontPath: p.documentFrontPath || "",
            documentBackUrl: p.documentBackUrl || "",
            documentBackPath: p.documentBackPath || "",
            epsDocumentUrl: p.epsDocumentUrl || "",
            epsDocumentPath: p.epsDocumentPath || "",
            address: p.address || "",
            emergencyContactName: p.emergencyContact?.name || "",
            emergencyContactRelationship:
              p.emergencyContact?.relationship || "Madre",
            emergencyContactCountryCode: emergencyPhoneData.code,
            emergencyContactPhone: emergencyPhoneData.number,
            clubId: member.clubId,
            periodId: member.periodId || "",
            unitId: member.unitId || "",
            role: member.role,
            clase: member.clase || "",
            status: member.status as "active" | "inactive" | "pending",
          };
        })()
      : {
          idDocumentType: "TI",
          bloodType: "O+",
          gender: "male",
          role: "member",
          status: "active",
          phoneCountryCode: "+57",
          emergencyContactCountryCode: "+57",
          emergencyContactRelationship: "Madre",
          clubId: directorClubId || "",
        },
  });

  const watchClubId = watch("clubId");
  const watchBirthDate = watch("birthDate");
  const selectedClub = clubs?.find((c) => c.id === watchClubId);

  // Query de unidades dinámico basado en club seleccionado
  const { data: units } = useUnitsByClub(watchClubId || "");

  // Calcular edad
  const calculatedAge = useMemo(() => {
    if (!watchBirthDate) return "";
    return calculateAge(new Date(watchBirthDate + "T12:00:00"));
  }, [watchBirthDate]);

  // Determinar tipo de club por edad y filtrar clubs
  const ageInYears = useMemo(() => {
    if (!watchBirthDate) return null;
    const birth = new Date(watchBirthDate + "T12:00:00");
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  }, [watchBirthDate]);

  const allowedClubType = useMemo(() => {
    if (ageInYears === null) return null;
    return getClubTypeByAge(ageInYears);
  }, [ageInYears]);

  const allowedClubTypes = useMemo(() => {
    if (ageInYears === null) return [] as string[];
    return getAllowedClubTypesByAge(ageInYears) as string[];
  }, [ageInYears]);

  // Derivar sección automáticamente de birthDate + club seleccionado.
  // Con el solapamiento en los 10 años, la sección real depende del club.
  const derivedSection = useMemo(() => {
    if (!watchBirthDate) return null;
    const birth = new Date(watchBirthDate + "T12:00:00");
    if (selectedClub) return resolveSectionForClub(birth, selectedClub);
    return deriveSectionFromBirthDate(birth);
  }, [watchBirthDate, selectedClub]);

  // Derivar memberType de la edad
  const derivedMemberType = useMemo(() => {
    if (ageInYears === null) return "acampante";
    return ageInYears >= 16 ? "lider" : "acampante";
  }, [ageInYears]);

  const filteredClubs = useMemo(() => {
    if (!clubs) return [];
    // RBAC: limitar a clubs gestionables según rol
    const manageable = filterManageableClubs(clubs, profile?.roleAssignments);
    if (allowedClubTypes.length === 0) return manageable;
    // Filtrar clubs que tengan alguna sección activa permitida para la edad
    // (incluye solapamientos: 10 años → aventureros o conquistadores)
    const filtered = manageable.filter((c) => {
      const sections = getActiveSections(c);
      return sections.some((s) => allowedClubTypes.includes(s));
    });
    // En modo edición: si el club actual del miembro no está en la lista
    // filtrada (p.ej. la sección quedó inactiva después), igual lo
    // mostramos para no perder el valor seleccionado.
    if (isEditing && watchClubId) {
      const current = clubs.find((c) => c.id === watchClubId);
      if (current && !filtered.some((c) => c.id === current.id)) {
        return [...filtered, current];
      }
    }
    return filtered;
  }, [
    clubs,
    allowedClubTypes,
    isEditing,
    watchClubId,
    profile?.roleAssignments,
  ]);

  // Auto-clear club si no coincide con la edad.
  // En modo edición NO limpiamos: el director debe poder editar al miembro
  // aunque el club tenga la sección desactivada (caso real con líderes
  // adultos en clubes marcados solo como conquistadores).
  useEffect(() => {
    if (isEditing) return;
    if (allowedClubTypes.length === 0 || !watchClubId || directorClubId) return;
    const currentClub = clubs?.find((c) => c.id === watchClubId);
    const sections = currentClub ? getActiveSections(currentClub) : [];
    if (currentClub && !sections.some((s) => allowedClubTypes.includes(s))) {
      setValue("clubId", "");
    }
  }, [
    allowedClubTypes,
    watchClubId,
    clubs,
    directorClubId,
    setValue,
    isEditing,
  ]);

  // Si es director de club, pre-seleccionar su club
  useEffect(() => {
    if (directorClubId && !isEditing) {
      setValue("clubId", directorClubId);
    }
  }, [directorClubId, isEditing, setValue]);

  /**
   * F14: la persona ya es miembro de otro club. En lugar de la doble
   * membresía, se le propone al director pedir el traslado — mostrando a quién
   * llamar en el club de origen, porque esto casi siempre se habla antes.
   */
  const ofrecerTraslado = async (person: Person, membership: Member) => {
    const destino = clubs?.find((c) => c.id === watchClubId);
    if (!destino) return;
    const [origen, contacto] = await Promise.all([
      clubService.getById(membership.clubId).catch(() => null),
      getDirectorContact(membership.clubId).catch(() => ({}) as never),
    ]);
    const nombreOrigen = origen?.name ?? "su club actual";
    const contactoLinea = contacto?.name
      ? ` Coordina con ${contacto.name}${contacto.phone ? ` · ${contacto.phone}` : ""}.`
      : "";
    const acudienteLinea = person.guardianUserId
      ? ` Está a cargo de un acudiente: el vínculo se conserva tal cual al ` +
        `trasladar, pero acuérdalo primero con la familia.`
      : "";

    const ok = await confirm({
      title: "Ya pertenece a otro club",
      description:
        `${person.fullName} (${person.idDocumentType} ${person.idDocumentNumber}) ` +
        `es miembro de ${nombreOrigen}. Para traerla a ${destino.name} sin ` +
        `duplicar su ficha, envía una solicitud de traslado: cuando el club de ` +
        `origen la apruebe, la membresía pasará completa y automática.` +
        acudienteLinea +
        contactoLinea,
      confirmText: "Solicitar traslado",
      cancelText: "Ahora no",
      variant: "warning",
    });
    if (!ok) return;

    try {
      // El teléfono del solicitante sale de su propia ficha (si la tiene).
      let miTelefono: string | undefined;
      if (profile?.personId) {
        miTelefono = (
          await personService.getById(profile.personId).catch(() => null)
        )?.phone;
      }
      await memberTransferService.request({
        person,
        membership: {
          id: membership.id,
          clubId: membership.clubId,
          role: membership.role,
        },
        originClubName: nombreOrigen,
        targetClubId: destino.id,
        targetClubName: destino.name,
        requestedBy: user!.uid,
        requestedByName: profile?.displayName || user?.email || "Director",
        requestedByPhone: miTelefono,
        requestedByEmail: user?.email ?? undefined,
      });
      toast.success(
        `Solicitud de traslado enviada a ${nombreOrigen}. ` +
          `La verás en Solicitudes → Traslados.`,
        { duration: 6000 },
      );
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo crear la solicitud");
    }
  };

  const onSubmit = async (data: MemberForm) => {
    if (!user) return;

    const club = clubs?.find((c) => c.id === data.clubId);
    if (!club) {
      toast.error("Club no encontrado");
      return;
    }

    // Foto y documentos: se AVISA, no se bloquea. El director suele registrar
    // a su gente —empezando por sí mismo— antes de tener los papeles a mano, y
    // exigirlos aquí le impedía dejar la lista armada. Se completan después:
    // al hacer el check-in del evento, o cuando la persona entre con su cuenta.
    const missing: string[] = [];
    if (!data.photoUrl && !pendingPhotoFile) missing.push("la foto tipo carnet");
    if (!data.documentFrontUrl && !pendingDocumentFile)
      missing.push("el documento por el anverso");
    if (!data.documentBackUrl && !pendingDocumentBackFile)
      missing.push("el documento por el reverso");
    if (missing.length > 0) {
      const seguir = await confirm({
        title: `Falta ${missing.length === 1 ? "un archivo" : `${missing.length} archivos`}`,
        description:
          `Vas a guardar sin ${missing.join(", ")}. ` +
          `Puedes subirlos más adelante desde la ficha, en el check-in del ` +
          `evento, o la propia persona al entrar con su cuenta. ` +
          `Ten en cuenta que sin la foto no se puede generar el carnet del club.`,
        confirmText: "Guardar así",
        cancelText: "Volver y subirlos",
        variant: "warning",
      });
      if (!seguir) {
        const el = document.getElementById("member-photo-slot");
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
    }

    try {
      const now = Timestamp.now();
      const birthDate = Timestamp.fromDate(
        new Date(data.birthDate + "T12:00:00"),
      );
      // El servicio personService normaliza los nombres automáticamente
      const fullName = `${data.firstName} ${data.lastName}`;

      // Combinar código de país con teléfonos
      const phoneWithCode = `${data.phoneCountryCode} ${data.phone}`;
      const emergencyPhoneWithCode = `${data.emergencyContactCountryCode} ${data.emergencyContactPhone}`;

      // ===== NUEVA ARQUITECTURA: Person + Member separados =====

      // 1. Buscar si ya existe una Person con este documento
      let personId: string | undefined;

      if (isEditing && member?.personId) {
        // Guardrail anti-reutilización: si se cambian a la vez el NOMBRE y el
        // DOCUMENTO a algo totalmente distinto, probablemente se está usando este
        // registro para OTRA persona (anti-patrón que deja inscripciones con el
        // nombre viejo). Confirmar antes de continuar.
        const orig = member.person;
        const origNum = (orig?.idDocumentNumber || "").replace(/\D/g, "");
        const newNum = (data.idDocumentNumber || "").replace(/\D/g, "");
        const origName = (orig?.fullName || "").trim().toLowerCase();
        const newName = fullName.trim().toLowerCase();
        const docChanged = !!origNum && !!newNum && origNum !== newNum;
        const origTokens = new Set(origName.split(/\s+/).filter(Boolean));
        const nameFullyDifferent =
          !!origName &&
          !!newName &&
          !newName
            .split(/\s+/)
            .filter(Boolean)
            .some((tok) => origTokens.has(tok));
        if (docChanged && nameFullyDifferent) {
          const ok = window.confirm(
            `Estás cambiando el nombre Y el documento de "${orig?.fullName}" a "${fullName}". ` +
              `Si es OTRA persona, cancela y usa "Agregar miembro": editar reutiliza este registro y deja inscripciones/consentimientos con el nombre viejo. ` +
              `¿Continuar de todos modos?`,
          );
          if (!ok) return;
        }
        // Si estamos editando y ya tiene personId, usarlo
        personId = member.personId;
        // Actualizar datos de la persona con los cambios del formulario
        await personService.update(personId, {
          firstName: data.firstName,
          lastName: data.lastName,
          fullName,
          // El documento también debe poder corregirse al editar. El servicio
          // normaliza y valida unicidad (lanza error claro si ya existe).
          idDocumentType: data.idDocumentType as IdDocumentType,
          idDocumentNumber: data.idDocumentNumber,
          memberType: derivedMemberType as MemberType,
          bloodType: data.bloodType as BloodType,
          birthDate,
          gender: data.gender as Gender,
          phone: phoneWithCode,
          healthInsurance: data.healthInsurance || "",
          email: data.email || "",
          photoUrl: data.photoUrl || "",
          address: data.address || "",
          emergencyContact: {
            name: data.emergencyContactName,
            relationship: data.emergencyContactRelationship,
            phone: emergencyPhoneWithCode,
          },
          updatedAt: now,
          updatedBy: user.uid,
        });
      } else if (selectedExistingMember?.personId) {
        // Si seleccionamos un miembro existente del club, usar su personId
        personId = selectedExistingMember.personId;
      } else {
        // Buscar persona existente por documento. Reuso cross-type (TI/CC
        // comparten número en Colombia) e incluye fichas inactivas/huérfanas,
        // para REUTILIZAR (y reactivar) en vez de chocar contra la guarda de
        // unicidad al crear.
        const existingPerson = await personService.findReusableByDocument(
          data.idDocumentType as IdDocumentType,
          data.idDocumentNumber,
        );

        // F14: si la persona ya es miembro (no inactivo) de OTRO club, aquí se
        // frenaba antes el flujo "sucio": se le creaba una segunda membresía
        // activa y de paso se le sobreescribía la ficha con lo tecleado. Ahora
        // el camino es la solicitud de traslado al club de origen.
        if (existingPerson) {
          const otras = (
            await memberService.getByPerson(existingPerson.id)
          ).filter((m) => m.status !== "inactive" && m.clubId !== data.clubId);
          if (otras.length > 0) {
            await ofrecerTraslado(existingPerson, otras[0]);
            return;
          }
        }

        if (existingPerson) {
          personId = existingPerson.id;
          // Actualizar datos de la persona si cambió algo (y reactivar si estaba
          // inactiva/huérfana). El servicio normaliza los campos automáticamente.
          await personService.update(existingPerson.id, {
            firstName: data.firstName,
            lastName: data.lastName,
            fullName,
            memberType: derivedMemberType as MemberType,
            bloodType: data.bloodType as BloodType,
            birthDate,
            gender: data.gender as Gender,
            phone: phoneWithCode,
            healthInsurance: data.healthInsurance || "",
            email: data.email || "",
            photoUrl: data.photoUrl || "",
            address: data.address || "",
            emergencyContact: {
              name: data.emergencyContactName,
              relationship: data.emergencyContactRelationship,
              phone: emergencyPhoneWithCode,
            },
            status: "active",
            updatedAt: now,
            updatedBy: user.uid,
          });
        } else {
          // Crear nueva Person
          // El servicio normaliza los campos automáticamente
          const personData: Record<string, unknown> = {
            firstName: data.firstName,
            lastName: data.lastName,
            fullName,
            memberType: derivedMemberType as MemberType,
            idDocumentType: data.idDocumentType as IdDocumentType,
            idDocumentNumber: data.idDocumentNumber,
            bloodType: data.bloodType as BloodType,
            birthDate,
            gender: data.gender as Gender,
            phone: phoneWithCode,
            healthInsurance: data.healthInsurance || "",
            email: data.email || "",
            photoUrl: data.photoUrl || "",
            address: data.address || "",
            emergencyContact: {
              name: data.emergencyContactName,
              relationship: data.emergencyContactRelationship,
              phone: emergencyPhoneWithCode,
            },
            // Ubicación organizacional (donde se registra)
            churchId: club.churchId || "",
            associationId: club.associationId || "",
            status: "active",
          };

          // Solo agregar linkedUserId si hay un usuario seleccionado
          if (selectedSystemUser?.id) {
            personData.linkedUserId = selectedSystemUser.id;
            personData.linkedAt = now;
          }

          personId = await createPersonMutation.mutateAsync({
            data: personData as Omit<Person, "id" | "createdAt" | "updatedAt">,
            userId: user.uid,
          });
        }
      }

      // 2.5. Subir archivos pendientes si existen
      if (
        personId &&
        (pendingPhotoFile ||
          pendingDocumentFile ||
          pendingDocumentBackFile ||
          pendingEpsFile)
      ) {
        setUploadingFiles(true);
        const fileUpdates: Record<string, unknown> = {};

        try {
          // Subir foto tipo carnet (NO encriptada — es pública para el carnet)
          if (pendingPhotoFile) {
            const result = await uploadMemberPhoto(pendingPhotoFile, personId);
            fileUpdates.photoUrl = result.url;
            fileUpdates.photoPath = result.path;
            data.photoUrl = result.url;
          }

          // Subir foto del documento de identidad — anverso (ENCRIPTADA)
          if (pendingDocumentFile) {
            const result = await uploadDocumentFront(
              pendingDocumentFile,
              personId,
            );
            fileUpdates.documentFrontUrl = result.url;
            fileUpdates.documentFrontPath = result.path;
            fileUpdates.documentFrontEncryption = result.encryption;
            fileUpdates.documentFrontContentType = pendingDocumentFile.type;
          }

          // Subir foto del documento de identidad — reverso (ENCRIPTADA)
          if (pendingDocumentBackFile) {
            const result = await uploadDocumentBack(
              pendingDocumentBackFile,
              personId,
            );
            fileUpdates.documentBackUrl = result.url;
            fileUpdates.documentBackPath = result.path;
            fileUpdates.documentBackEncryption = result.encryption;
            fileUpdates.documentBackContentType = pendingDocumentBackFile.type;
          }

          // Subir soporte de EPS (ENCRIPTADO)
          if (pendingEpsFile) {
            const result = await uploadEpsDocument(pendingEpsFile, personId);
            fileUpdates.epsDocumentUrl = result.url;
            fileUpdates.epsDocumentPath = result.path;
            fileUpdates.epsDocumentEncryption = result.encryption;
            fileUpdates.epsDocumentContentType = pendingEpsFile.type;
          }

          // Actualizar Person con las URLs de los archivos
          if (Object.keys(fileUpdates).length > 0) {
            await personService.update(personId, {
              ...fileUpdates,
              updatedAt: now,
              updatedBy: user.uid,
            });

            // Sincronizar personPhoto en todos los Members de esta persona
            // (importante si la persona tiene membresías en múltiples clubes)
            if (fileUpdates.photoUrl) {
              await memberService.syncPersonData(
                personId,
                fullName,
                fileUpdates.photoUrl as string,
              );
            }
          }
        } finally {
          setUploadingFiles(false);
        }
      }

      // 3. Preparar datos del Member (solo datos de membresía)
      // Con memberRolesEnabled en edición: `member.role` es derivado de `memberRoles`
      // (sync via CF `onMemberRolesWrite`); preservamos el valor actual para no pisar la sincronización.
      const preserveLegacyRole =
        isEditing && memberRolesEnabled && !!member?.role;
      const memberData = {
        personId: personId!,
        clubId: data.clubId,
        churchId: club.churchId || "",
        associationId: club.associationId || "",
        section: (derivedSection ||
          (club.sections
            ? Object.entries(club.sections).find(([, s]) => s.active)?.[0]
            : club.type) ||
          "conquistadores") as ClubType, // new: derived from birthDate
        role: (preserveLegacyRole ? member!.role : data.role) as MemberRole,
        status: data.status,
        // Datos desnormalizados para listas rápidas
        personName: fullName,
        personPhoto: data.photoUrl || "",
      };

      // Agregar campos opcionales solo si tienen valor
      const optionalFields: Record<string, unknown> = {};
      if (data.unitId) optionalFields.unitId = data.unitId;
      if (data.periodId) optionalFields.periodId = data.periodId;
      if (data.clase) optionalFields.clase = data.clase as MemberClass;

      if (isEditing && member) {
        // Actualizar miembro existente
        await updateMutation.mutateAsync({
          id: member.id,
          data: {
            ...memberData,
            ...optionalFields,
          },
        });

        // Sincronizar datos de persona en todos los members
        await memberService.syncPersonData(personId!, fullName, data.photoUrl);
      } else if (selectedExistingMember) {
        // Actualizar miembro existente seleccionado desde búsqueda
        await updateMutation.mutateAsync({
          id: selectedExistingMember.id,
          data: {
            ...memberData,
            ...optionalFields,
          },
        });

        // Sincronizar datos de persona en todos los members
        await memberService.syncPersonData(personId!, fullName, data.photoUrl);
      } else {
        // Crear nuevo miembro - verificar duplicados primero
        if (personId) {
          const existingMembers = await memberService.getByPerson(personId);
          const duplicate = existingMembers.find(
            (m) => m.clubId === data.clubId && m.status !== "inactive",
          );
          if (duplicate) {
            toast.error(
              "Esta persona ya tiene una membresía activa en este club",
            );
            return;
          }
        }

        await createMemberMutation.mutateAsync({
          data: {
            ...memberData,
            ...optionalFields,
            roleStartDate: now,
            registrationDate: now,
            registrationPaid: false,
          } as Omit<Member, "id" | "createdAt" | "updatedAt">,
          userId: user.uid,
        });
      }

      handleClose();
    } catch (error) {
      console.error("Error en flujo de miembro:", error);
      const msg =
        error instanceof Error
          ? error.message
          : "Error desconocido al guardar miembro";
      toast.error(`No se pudo guardar: ${msg}`, { duration: 6000 });
    }
  };

  // Mostrar feedback visible cuando la validación de zod bloquea el submit
  const FIELD_LABELS: Record<string, string> = {
    firstName: "Nombres",
    lastName: "Apellidos",
    idDocumentType: "Tipo de documento",
    idDocumentNumber: "Número de documento",
    birthDate: "Fecha de nacimiento",
    gender: "Género",
    bloodType: "Tipo de sangre",
    phone: "Teléfono",
    phoneCountryCode: "Código de país (teléfono)",
    address: "Dirección",
    healthInsurance: "EPS",
    email: "Email",
    emergencyContactName: "Nombre del contacto de emergencia",
    emergencyContactRelationship: "Parentesco del contacto de emergencia",
    emergencyContactPhone: "Teléfono del contacto de emergencia",
    emergencyContactCountryCode: "Código de país (emergencia)",
    clubId: "Club",
    role: "Rol",
    status: "Estado",
    photoUrl: "Foto del miembro",
  };
  const onInvalid = (formErrors: Record<string, unknown>) => {
    const keys = Object.keys(formErrors);
    if (keys.length === 0) return;
    const labels = keys.slice(0, 4).map((k) => FIELD_LABELS[k] ?? k);
    const more = keys.length > 4 ? ` y ${keys.length - 4} más` : "";
    toast.error(`Faltan o son inválidos: ${labels.join(", ")}${more}.`, {
      duration: 6000,
    });
    // Scroll al primer campo con error si está renderizado
    const firstKey = keys[0];
    const el = document.querySelector(
      `[name="${firstKey}"]`,
    ) as HTMLElement | null;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      try {
        el.focus();
      } catch {}
    }
  };

  const handleClose = () => {
    reset();
    setStep(isEditing ? "form" : "search");
    setSearchTerm("");
    setSelectedExistingMember(null);
    setSelectedSystemUser(null);
    setPendingPhotoFile(null);
    setPendingDocumentFile(null);
    setPendingDocumentBackFile(null);
    setPendingEpsFile(null);
    onClose();
  };

  const handleSelectExistingMember = (existingMember: MemberWithPerson) => {
    // Pre-llenar el formulario con datos del miembro existente
    const p = existingMember.person;
    setSelectedExistingMember(existingMember);
    setSelectedSystemUser(null);
    setValue("firstName", p.firstName || "");
    setValue("lastName", p.lastName || "");
    setValue("email", p.email || "");
    // Separar código de país del teléfono
    const phoneData = extractCountryCode(p.phone);
    setValue("phoneCountryCode", phoneData.code);
    setValue("phone", phoneData.number);
    setValue(
      "idDocumentType",
      (p.idDocumentType?.toUpperCase() as
        | "CC"
        | "TI"
        | "CE"
        | "PASAPORTE"
        | "PPT"
        | "RC") || "TI",
    );
    setValue("idDocumentNumber", p.idDocumentNumber || "");
    setValue("bloodType", p.bloodType || "O+");
    setValue("gender", mapGender(p.gender));
    setValue("address", p.address || "");
    setValue("healthInsurance", p.healthInsurance || "");
    if (p.birthDate) {
      setValue("birthDate", formatDateForInput(p.birthDate));
    }
    if (p.emergencyContact) {
      setValue("emergencyContactName", p.emergencyContact.name || "");
      setValue(
        "emergencyContactRelationship",
        p.emergencyContact.relationship || "",
      );
      const emergencyPhoneData = extractCountryCode(p.emergencyContact.phone);
      setValue("emergencyContactCountryCode", emergencyPhoneData.code);
      setValue("emergencyContactPhone", emergencyPhoneData.number);
    }
    // Pre-llenar datos de membresía del miembro existente
    setValue("role", existingMember.role || "member");
    setValue(
      "status",
      (existingMember.status as "active" | "inactive" | "pending") || "active",
    );
    if (existingMember.clase) setValue("clase", existingMember.clase);
    if (existingMember.unitId) setValue("unitId", existingMember.unitId);
    if (existingMember.periodId) setValue("periodId", existingMember.periodId);
    setStep("form");
  };

  const handleSelectSystemUser = (systemUser: {
    id: string;
    displayName: string;
    email: string;
    phone?: string;
  }) => {
    // Pre-llenar el formulario con datos del usuario del sistema
    setSelectedSystemUser(systemUser);
    setSelectedExistingMember(null);

    // Separar displayName en firstName y lastName
    const nameParts = (systemUser.displayName || "").trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    setValue("firstName", firstName);
    setValue("lastName", lastName);
    setValue("email", systemUser.email || "");
    setValue("phone", systemUser.phone || "");
    // El resto de campos se dejan vacíos para que los complete el director
    setStep("form");
  };

  const handleCreateNew = () => {
    setSelectedExistingMember(null);
    setSelectedSystemUser(null);
    setStep("form");
  };

  if (!isOpen) return null;

  // PASO 1: Búsqueda de miembro existente
  if (step === "search" && !isEditing) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div
          role="dialog"
          aria-modal="true"
          className="bg-white rounded-2xl max-w-lg w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        >
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-md shadow-purple-500/25">
                <Search className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Nuevo Miembro
                </h2>
                <p className="text-sm font-medium text-gray-500">
                  Busca si el miembro ya existe o crea uno nuevo
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Contenido de búsqueda */}
          <div className="p-6 space-y-4">
            {/* Campo de búsqueda */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar por nombre, email o documento
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Ej: Juan Pérez o juan@email.com"
                  className="pl-10"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Escribe al menos 2 caracteres para buscar
              </p>
            </div>

            {/* Resultados de búsqueda */}
            {searchTerm.length >= 2 && (
              <div className="space-y-3">
                {/* Miembros del club */}
                {filteredMembers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Miembros del club ({filteredMembers.length})
                    </p>
                    {filteredMembers.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => handleSelectExistingMember(m)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors duration-200 text-left"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-purple-500/25">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {m.person.fullName ||
                              `${m.person.firstName} ${m.person.lastName}`}
                          </p>
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            {m.person.email && (
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3" />
                                {m.person.email}
                              </span>
                            )}
                          </div>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700">
                          Ya en el club
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}

                {/* Usuarios registrados en la plataforma (no en el club) */}
                {filteredSystemUsers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Usuarios de la plataforma ({filteredSystemUsers.length})
                    </p>
                    {filteredSystemUsers.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => handleSelectSystemUser(u)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200 text-left"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-blue-500/25">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {u.displayName || "Sin nombre"}
                          </p>
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                            {u.email && (
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3" />
                                {u.email}
                              </span>
                            )}
                          </div>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">
                          Usuario registrado
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}

                {/* Sin resultados */}
                {filteredMembers.length === 0 &&
                  filteredSystemUsers.length === 0 && (
                    <p className="text-sm font-medium text-gray-500 text-center py-2">
                      No se encontraron resultados
                    </p>
                  )}
              </div>
            )}

            {/* Botón crear nuevo */}
            <div className="pt-4 border-t border-gray-200">
              <Button onClick={handleCreateNew} className="w-full gap-2">
                <Plus className="w-4 h-4" />
                Crear Miembro Nuevo
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Si el miembro no existe, créalo desde cero
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PASO 2: Formulario de miembro
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            {!isEditing && (
              <button
                onClick={() => setStep("search")}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200 mr-1"
                title="Volver a buscar"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-md shadow-primary-500/25">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing
                  ? "Editar Miembro"
                  : selectedExistingMember
                    ? "Editar Miembro Existente"
                    : "Nuevo Miembro"}
              </h2>
              <p className="text-sm font-medium text-gray-500">
                {isEditing
                  ? "Modifica los datos del miembro"
                  : selectedExistingMember
                    ? `Editando datos de ${selectedExistingMember.person.fullName || selectedExistingMember.person.firstName}`
                    : "Registra un nuevo miembro en el club"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          className="p-6 space-y-6"
        >
          {/* ===== INFORMACIÓN PERSONAL ===== */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider border-b pb-2">
              Información Personal
            </h3>

            {/* Foto tipo carnet + Documentos */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 space-y-4">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <User className="w-4 h-4 text-primary-600" />
                Foto y Documentos del Miembro
              </h4>

              {/* Banner: documentos obligatorios que faltan */}
              {(() => {
                const items: string[] = [];
                if (!watch("photoUrl") && !pendingPhotoFile)
                  items.push("Foto tipo carnet");
                if (!watch("documentFrontUrl") && !pendingDocumentFile)
                  items.push("Documento — Anverso");
                if (!watch("documentBackUrl") && !pendingDocumentBackFile)
                  items.push("Documento — Reverso");
                if (items.length === 0) return null;
                return (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-800">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold">
                        Faltan {items.length} archivo{items.length !== 1 ? "s" : ""} por subir
                      </p>
                      <ul className="list-disc list-inside text-amber-700 mt-0.5">
                        {items.map((it) => (
                          <li key={it}>{it}</li>
                        ))}
                      </ul>
                      <p className="text-amber-700 mt-1">
                        Puedes guardar sin ellos y subirlos después. Eso sí, sin
                        la <b>foto</b> no se puede generar el carnet del club.
                      </p>
                    </div>
                  </div>
                );
              })()}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Foto tipo carnet - La más importante (obligatoria) */}
                <div id="member-photo-slot" className="md:row-span-2">
                  <DocumentUpload
                    currentUrl={
                      pendingPhotoFile
                        ? URL.createObjectURL(pendingPhotoFile)
                        : watch("photoUrl")
                    }
                    title="Foto Tipo Carnet"
                    description="Necesaria para el carnet — fondo claro, rostro visible"
                    icon={<User className="w-6 h-6 text-primary-500" />}
                    onUpload={async (file) => {
                      if (isEditing && member?.personId) {
                        const result = await uploadMemberPhoto(
                          file,
                          member.personId,
                        );
                        setValue("photoUrl", result.url);
                        setValue("photoPath", result.path);
                        return result;
                      } else {
                        setPendingPhotoFile(file);
                        return { url: URL.createObjectURL(file), path: "" };
                      }
                    }}
                    onRemove={() => {
                      setValue("photoUrl", "");
                      setValue("photoPath", "");
                      setPendingPhotoFile(null);
                    }}
                    className="h-full"
                  />
                </div>

                {/* Documento de identidad — Anverso */}
                <DocumentUpload
                  currentUrl={
                    pendingDocumentFile
                      ? URL.createObjectURL(pendingDocumentFile)
                      : watch("documentFrontUrl")
                  }
                  encryption={
                    member?.person?.documentFrontEncryption as
                      | EncryptionMeta
                      | undefined
                  }
                  contentType={member?.person?.documentFrontContentType}
                  title="Documento — Anverso"
                  description="Foto legible del frente (encriptada)"
                  icon={<CreditCard className="w-6 h-6 text-gray-400" />}
                  onUpload={async (file) => {
                    if (isEditing && member?.personId) {
                      const result = await uploadDocumentFront(
                        file,
                        member.personId,
                      );
                      setValue("documentFrontUrl", result.url);
                      setValue("documentFrontPath", result.path);
                      // Guardar URL, path y metadata de encriptación en Person
                      await personService.update(member.personId, {
                        documentFrontUrl: result.url,
                        documentFrontPath: result.path,
                        documentFrontEncryption: result.encryption,
                        documentFrontContentType: file.type,
                      });
                      return result;
                    } else {
                      setPendingDocumentFile(file);
                      return { url: URL.createObjectURL(file), path: "" };
                    }
                  }}
                  onRemove={() => {
                    setValue("documentFrontUrl", "");
                    setValue("documentFrontPath", "");
                    setPendingDocumentFile(null);
                  }}
                />

                {/* Documento de identidad — Reverso */}
                <DocumentUpload
                  currentUrl={
                    pendingDocumentBackFile
                      ? URL.createObjectURL(pendingDocumentBackFile)
                      : watch("documentBackUrl")
                  }
                  encryption={
                    member?.person?.documentBackEncryption as
                      | EncryptionMeta
                      | undefined
                  }
                  contentType={member?.person?.documentBackContentType}
                  title="Documento — Reverso"
                  description="Foto legible del dorso (encriptada)"
                  icon={<CreditCard className="w-6 h-6 text-gray-400" />}
                  onUpload={async (file) => {
                    if (isEditing && member?.personId) {
                      const result = await uploadDocumentBack(
                        file,
                        member.personId,
                      );
                      setValue("documentBackUrl", result.url);
                      setValue("documentBackPath", result.path);
                      await personService.update(member.personId, {
                        documentBackUrl: result.url,
                        documentBackPath: result.path,
                        documentBackEncryption: result.encryption,
                        documentBackContentType: file.type,
                      });
                      return result;
                    } else {
                      setPendingDocumentBackFile(file);
                      return { url: URL.createObjectURL(file), path: "" };
                    }
                  }}
                  onRemove={() => {
                    setValue("documentBackUrl", "");
                    setValue("documentBackPath", "");
                    setPendingDocumentBackFile(null);
                  }}
                />

                {/* Soporte de EPS */}
                <DocumentUpload
                  currentUrl={
                    pendingEpsFile
                      ? URL.createObjectURL(pendingEpsFile)
                      : watch("epsDocumentUrl")
                  }
                  encryption={
                    member?.person?.epsDocumentEncryption as
                      | EncryptionMeta
                      | undefined
                  }
                  contentType={member?.person?.epsDocumentContentType}
                  title="Soporte de EPS"
                  description="Carnet o certificado (encriptado)"
                  icon={<FileText className="w-6 h-6 text-gray-400" />}
                  acceptPdf
                  onUpload={async (file) => {
                    if (isEditing && member?.personId) {
                      const result = await uploadEpsDocument(
                        file,
                        member.personId,
                      );
                      setValue("epsDocumentUrl", result.url);
                      setValue("epsDocumentPath", result.path);
                      // Guardar URL, path y metadata de encriptación en Person
                      await personService.update(member.personId, {
                        epsDocumentUrl: result.url,
                        epsDocumentPath: result.path,
                        epsDocumentEncryption: result.encryption,
                        epsDocumentContentType: file.type,
                      });
                      return result;
                    } else {
                      setPendingEpsFile(file);
                      return { url: URL.createObjectURL(file), path: "" };
                    }
                  }}
                  onRemove={() => {
                    setValue("epsDocumentUrl", "");
                    setValue("epsDocumentPath", "");
                    setPendingEpsFile(null);
                  }}
                />
              </div>

              <p className="text-xs text-gray-500 italic">
                📷 Los documentos se guardarán de forma segura y solo serán
                visibles para directores del club.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <Input
                  {...register("firstName")}
                  placeholder="Ej: Juan Carlos"
                  className={errors.firstName ? "border-red-500" : ""}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apellido *
                </label>
                <Input
                  {...register("lastName")}
                  placeholder="Ej: Pérez González"
                  className={errors.lastName ? "border-red-500" : ""}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.lastName.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Identificación *
                </label>
                <Select {...register("idDocumentType")}>
                  {Object.entries(ID_DOCUMENT_TYPE_LABELS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ),
                  )}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Identificación *
                </label>
                <Input
                  {...register("idDocumentNumber")}
                  placeholder="Ej: 1234567890"
                  className={errors.idDocumentNumber ? "border-red-500" : ""}
                />
                {errors.idDocumentNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.idDocumentNumber.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Sangre *
                </label>
                <Select {...register("bloodType")}>
                  {Object.entries(BLOOD_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <DatePickerField
                  control={control}
                  name="birthDate"
                  label="Fecha de Nacimiento *"
                  error={errors.birthDate?.message}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Edad
                </label>
                <Input
                  value={calculatedAge}
                  disabled
                  className="bg-gray-50"
                  placeholder="Se calcula automáticamente"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Género *
                </label>
                <Select {...register("gender")}>
                  {Object.entries(GENDER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <div className="flex gap-2">
                  <Select {...register("phoneCountryCode")} className="w-28">
                    {countryCodes.map(({ code, flag }) => (
                      <option key={code} value={code}>
                        {flag} {code}
                      </option>
                    ))}
                  </Select>
                  <Input
                    {...register("phone")}
                    placeholder="300 123 4567"
                    className={`flex-1 ${errors.phone ? "border-red-500" : ""}`}
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              <div>
                <EpsSelect
                  label="Sisbén o EPS"
                  value={watch("healthInsurance") || ""}
                  onChange={(v) =>
                    setValue("healthInsurance", v, { shouldDirty: true })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  {...register("email")}
                  placeholder="correo@ejemplo.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
                {!errors.email &&
                  user?.email &&
                  watch("email")?.trim().toLowerCase() ===
                    user.email.toLowerCase() && (
                    <p className="text-amber-600 text-xs mt-1">
                      ⚠️ Este es tu propio correo. Si registras a otra persona,
                      déjalo vacío para que quede como menor a tu cargo.
                    </p>
                  )}
                <p className="text-xs text-gray-500 mt-1">
                  Necesario para que el miembro pueda crear su cuenta y ver su
                  membresía automáticamente.
                </p>
              </div>
            </div>
          </div>

          {/* ===== INFORMACIÓN DE CONTACTO ===== */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider border-b pb-2">
              Información de Contacto
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección *
                </label>
                <Input
                  {...register("address")}
                  placeholder="Ej: Carrera 10 # 20 - 30, Barrio Centro"
                  className={errors.address ? "border-red-500" : ""}
                />
                {errors.address && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.address.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Persona de Contacto *
                </label>
                <Input
                  {...register("emergencyContactName")}
                  placeholder="Ej: María López"
                  className={
                    errors.emergencyContactName ? "border-red-500" : ""
                  }
                />
                {errors.emergencyContactName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.emergencyContactName.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relación con la Persona *
                </label>
                <Select
                  {...register("emergencyContactRelationship")}
                  className={
                    errors.emergencyContactRelationship ? "border-red-500" : ""
                  }
                >
                  <option value="">Seleccionar relación</option>
                  {relationships.map((rel) => (
                    <option key={rel.value} value={rel.value}>
                      {rel.label}
                    </option>
                  ))}
                </Select>
                {errors.emergencyContactRelationship && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.emergencyContactRelationship.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono de Contacto *
                </label>
                <div className="flex gap-2">
                  <Select
                    {...register("emergencyContactCountryCode")}
                    className="w-28"
                  >
                    {countryCodes.map(({ code, flag }) => (
                      <option key={code} value={code}>
                        {flag} {code}
                      </option>
                    ))}
                  </Select>
                  <Input
                    {...register("emergencyContactPhone")}
                    placeholder="Ej: 3001234567"
                    className={`flex-1 ${errors.emergencyContactPhone ? "border-red-500" : ""}`}
                  />
                </div>
                {errors.emergencyContactPhone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.emergencyContactPhone.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ===== DATOS DE MEMBRESÍA ===== */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider border-b pb-2">
              Datos de Membresía
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Club *
                </label>
                <Select
                  {...register("clubId")}
                  className={errors.clubId ? "border-red-500" : ""}
                  disabled
                >
                  <option value="">Seleccionar club...</option>
                  {filteredClubs.map((club) => (
                    <option key={club.id} value={club.id}>
                      {club.name} (
                      {getActiveSections(club)
                        .map((s) => CLUB_TYPE_LABELS[s as ClubType])
                        .join(", ")}
                      )
                    </option>
                  ))}
                </Select>
                {errors.clubId && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.clubId.message}
                  </p>
                )}
                {allowedClubType && !directorClubId && (
                  <p className="text-xs text-blue-600 mt-1">
                    📋 Por edad ({ageInYears} años), corresponde a{" "}
                    {CLUB_TYPE_LABELS[allowedClubType]}
                    {allowedClubTypes.length > 1 && (
                      <>
                        {" "}
                        — también puede ir a{" "}
                        {allowedClubTypes
                          .filter((s) => s !== allowedClubType)
                          .map((s) => CLUB_TYPE_LABELS[s as ClubType])
                          .join(", ")}
                      </>
                    )}
                  </p>
                )}
                {!watchBirthDate && !directorClubId && (
                  <p className="text-xs text-amber-600 mt-1">
                    Ingresa la fecha de nacimiento para filtrar clubs por edad
                  </p>
                )}
              </div>

              {!(isEditing && memberRolesEnabled) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo *
                  </label>
                  <Select
                    {...register("role")}
                    className={errors.role ? "border-red-500" : ""}
                  >
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>
                        {MEMBER_ROLE_LABELS[role]}
                      </option>
                    ))}
                  </Select>
                  {errors.role && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.role.message}
                    </p>
                  )}
                  {!isEditing && memberRolesEnabled && (
                    <p className="text-xs text-gray-500 mt-1">
                      Podrás añadir más cargos después de crear el miembro desde
                      el panel "Cargos en el club".
                    </p>
                  )}
                </div>
              )}

              {isEditing &&
                member?.id &&
                member?.personId &&
                memberRolesEnabled && (
                  <div className="md:col-span-2">
                    <MemberRolesPanel
                      memberId={member.id}
                      personId={member.personId}
                      clubId={member.clubId}
                      clubSections={
                        (selectedClub
                          ? getActiveSections(selectedClub)
                          : member.section
                            ? [member.section]
                            : []) as ClubType[]
                      }
                      personName={member.person?.fullName}
                    />
                  </div>
                )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clase/Nivel
                </label>
                <Select {...register("clase")} disabled>
                  <option value="">Sin clase asignada</option>
                  {selectedClub &&
                    derivedSection &&
                    classesByClubType[derivedSection]?.map((clase) => (
                      <option key={clase} value={clase}>
                        {MEMBER_CLASS_LABELS[clase]}
                      </option>
                    ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unidad
                </label>
                <Select {...register("unitId")} disabled>
                  <option value="">Sin unidad asignada</option>
                  {units?.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <Select {...register("status")} disabled>
                  <option value="active">Activo</option>
                  <option value="pending">Pendiente</option>
                  <option value="inactive">Inactivo</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Periodo Académico
                </label>
                <Select {...register("periodId")} disabled>
                  <option value="">Seleccionar periodo...</option>
                  {periods?.map((period) => (
                    <option key={period.id} value={period.id}>
                      {period.name} ({period.year})
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                uploadingFiles ||
                createPersonMutation.isPending ||
                createMemberMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              uploadingFiles ||
              createPersonMutation.isPending ||
              createMemberMutation.isPending ||
              updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {isEditing || selectedExistingMember
                    ? "Guardando..."
                    : "Registrando..."}
                </>
              ) : isEditing || selectedExistingMember ? (
                "Guardar Cambios"
              ) : (
                "Registrar Miembro"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
